// Recency rules for survey scores (CSAT / NPS), shared by the grid cells,
// the sort comparator, and the flag pill — and by the upcoming Attention
// column. One place owns the constants so the surfaces can never disagree.

// Surveys run roughly monthly; 60 days = two missed cycles. Older than this
// a score renders dimmed, never gets red-flag styling, and sinks below every
// fresh score when sorting.
export const SCORE_STALE_AFTER_DAYS = 60

// A score only counts toward "needs attention" while it is recent enough to
// act on (spec §5): CSAT <= 2 or NPS <= 6 submitted within this window.
export const SCORE_FLAG_WINDOW_DAYS = 30

const MS_PER_DAY = 1000 * 60 * 60 * 24

export function daysSince(timestamp: string): number | null {
  const time = new Date(timestamp).getTime()
  if (isNaN(time)) return null
  return Math.floor((Date.now() - time) / MS_PER_DAY)
}

// Calendar-day difference, midnight to midnight: yesterday 23:59 counts as
// 1d. This is how the Last Progress cell has always counted, so the stall
// and no-session attention signals use the same counter — the cell, the
// Attention chips and the pill can never disagree at a threshold boundary.
export function calendarDaysSince(timestamp: string): number | null {
  const then = new Date(timestamp)
  if (isNaN(then.getTime())) return null
  const now = new Date()
  const thenMidnight = new Date(
    then.getFullYear(),
    then.getMonth(),
    then.getDate(),
  )
  const nowMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round(
    (nowMidnight.getTime() - thenMidnight.getTime()) / MS_PER_DAY,
  )
}

export function isScoreStale(submittedAt: string): boolean {
  const days = daysSince(submittedAt)
  return days !== null && days > SCORE_STALE_AFTER_DAYS
}

export function isScoreFreshForFlag(submittedAt: string): boolean {
  const days = daysSince(submittedAt)
  return days !== null && days >= 0 && days <= SCORE_FLAG_WINDOW_DAYS
}

// Compact age for a grid cell: "3d" under a week, "3w" under 8 weeks,
// "3mo" beyond. Returns "" for an unparseable timestamp so callers can
// fall back to the bare score.
export function formatScoreAge(submittedAt: string): string {
  const days = daysSince(submittedAt)
  if (days === null) return ""
  if (days < 7) return `${Math.max(days, 0)}d`
  if (days < 56) return `${Math.floor(days / 7)}w`
  return `${Math.floor(days / 30)}mo`
}

// Spelled-out age for anywhere a human reads a sentence: "rated 4 weeks ago".
// "NPS 0 · 4w" sent Mayer asking what it even means — outside the tightest
// grid cells, ages are written in words.
export function formatScoreAgeLong(submittedAt: string): string {
  const days = daysSince(submittedAt)
  if (days === null) return ""
  if (days <= 0) return "today"
  if (days === 1) return "yesterday"
  if (days < 7) return `${days} days ago`
  const weeks = Math.floor(days / 7)
  if (days < 56) return weeks === 1 ? "1 week ago" : `${weeks} weeks ago`
  const months = Math.floor(days / 30)
  return months === 1 ? "1 month ago" : `${months} months ago`
}
